import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, message, Input, Grid, Space, Flex, Pagination, Card, Tooltip, Popover } from 'antd';
import { PlusOutlined, SearchOutlined, CompassOutlined, EllipsisOutlined } from '@ant-design/icons';
import debounce from 'lodash/debounce';
import { useGlobal } from '../Shared/GlobalContext/GlobalContext';
import { getAgents, createOrUpdateAgent, deleteAgent } from './AgentServices';
import AgentList from './AgentList/AgentList';
import AddAgentModal from './AddAgent/AddAgent';
import { useErrorHandler } from '../../Hooks/useErrorHandler';
import { useTranslation } from 'react-i18next';
import useSocket from '../../Hooks/useSocket';
import useSocketSubscription from '../../Hooks/useSocketSubscription';
import useSocketEvent from '../../Hooks/useSocketEvent';
import { Resource, Action } from '../../Constants/socket';
import { resolveBackendMessage } from '../../Utils/apiMessages';
import SharedFilterBar from '../Shared/Filters/SharedFilterBar';
import { agentFilterFields } from '../Shared/Filters/filterConfig';
import ExportButton from '../DataExportImport/ExportButton';
import ImportButton from '../DataExportImport/ImportButton';

const { useBreakpoint } = Grid;

const AgentsPage = () => {
    const { t } = useTranslation();
    const screens = useBreakpoint();
    const { setLoading, user: { id: currentUserId } = {} } = useGlobal();
    const [skeleton, setSkeleton] = useState(true);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isAgentGuideOpen, setIsAgentGuideOpen] = useState(false);
    const { handleError } = useErrorHandler();
    const [agents, setAgents] = useState([]);
    const [metadata, setMetadata] = useState(null);
    const [searchText, setSearchText] = useState('');
    const searchTextRef = React.useRef('');
    const fetchAbortControllerRef = React.useRef(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [modalMode, setModalMode] = useState('create');
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 25,
        total: 0
    });
    const [filters, setFilters] = useState([]);
    const [normalizedFilters, setNormalizedFilters] = useState({});
    const [sortBy, setSortBy] = useState(undefined);
    const [sortDirection, setSortDirection] = useState(undefined);
    const [isMoreActionsOpen, setIsMoreActionsOpen] = useState(false);
    const { connectionState } = useSocket();
    const isSubscribed = useSocketSubscription(Resource.USER, currentUserId ? [currentUserId] : null, true);
    const agentIds = useMemo(() => agents.map(agent => agent.id), [agents]);
    useSocketSubscription(Resource.AGENT, agentIds);

    const fetchAgents = useCallback(async (page = 1, search = '', pageSize, filtersPayload = {}) => {
        // Cancel any in-flight request before starting a new one
        if (fetchAbortControllerRef.current) {
            fetchAbortControllerRef.current.abort();
        }
        const controller = new AbortController();
        fetchAbortControllerRef.current = controller;

        try {
            setLoading(true);
            const { data, metadata } = await getAgents({
                page,
                size: pageSize,
                search: search.trim(),
                filters: filtersPayload,
                sort_by: sortBy,
                sort_direction: sortDirection,
                signal: controller.signal,
            });
            setPagination({
                current: metadata.page,
                pageSize: metadata.page_size,
                total: metadata.total
            });
            setAgents(data);
            setMetadata(metadata);
        } catch (error) {
            // Silently ignore cancelled requests
            if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') return;
            handleError(error);
        } finally {
            if (fetchAbortControllerRef.current === controller) {
                setLoading(false);
                setSkeleton(false);
            }
        }
    }, [setLoading, handleError, sortBy, sortDirection]);

    // Cleanup abort controller on unmount
    useEffect(() => {
        return () => {
            if (fetchAbortControllerRef.current) {
                fetchAbortControllerRef.current.abort();
            }
        };
    }, []);

    // Handle new agents
    const handleAgentCreated = useCallback((data) => {
        fetchAgents(currentPage, searchTextRef.current, pagination.pageSize, normalizedFilters);
    }, [currentPage, fetchAgents, pagination.pageSize, normalizedFilters]);

    // Handle updated agents
    const handleAgentUpdated = useCallback((data) => {
        setAgents(prev =>
            prev.map(agent => agent.id === data.id ? data : agent)
        );
    }, []);

    // Handle deleted agents
    const handleAgentDeleted = useCallback((data) => {
        fetchAgents(currentPage, searchTextRef.current, pagination.pageSize, normalizedFilters);
    }, [currentPage, fetchAgents, pagination.pageSize, normalizedFilters]);

    // Register socket event listeners
    useSocketEvent(Resource.AGENT, Action.CREATE, handleAgentCreated);
    useSocketEvent(Resource.AGENT, Action.UPDATE, handleAgentUpdated);
    useSocketEvent(Resource.AGENT, Action.DELETE, handleAgentDeleted);

    const handlePageChange = (page, pageSize) => {
        setPagination(prev => ({
            ...prev,
            current: page,
            pageSize: pageSize
        }));
        setCurrentPage(page);
    };

    const debouncedSearch = useCallback(
        debounce((searchValue, filtersPayload, pageSize) => {
            setCurrentPage(1);
            setSkeleton(true);
            fetchAgents(1, searchValue, pageSize, filtersPayload);
        }, 500),
        [fetchAgents]
    );

    const debouncedFilterChange = useCallback(
        debounce((search, pageSize, filtersPayload) => {
            setCurrentPage(1);
            setSkeleton(true);
            fetchAgents(1, search, pageSize, filtersPayload);
        }, 500),
        [fetchAgents]
    );

    // Fetch on page/sort/filter changes (NOT on search — that's handled by debounce)
    useEffect(() => {
        fetchAgents(currentPage, searchTextRef.current, pagination.pageSize, normalizedFilters);
    }, [currentPage, fetchAgents, pagination.pageSize]);

    const handleSearchChange = useCallback((e) => {
        const value = e.target.value;
        setSearchText(value);
        searchTextRef.current = value;
        debouncedSearch(value, normalizedFilters, pagination.pageSize);
    }, [debouncedSearch, normalizedFilters, pagination.pageSize]);

    const handleFiltersChange = useCallback((nextFilters, normalized) => {
        setFilters(nextFilters);
        setNormalizedFilters(normalized);
        debouncedFilterChange(searchTextRef.current, pagination.pageSize, normalized);
    }, [debouncedFilterChange, pagination.pageSize]);

    const handleSortChange = useCallback(({ sort_by, sort_direction }) => {
        setSortBy(sort_by || undefined);
        setSortDirection(sort_by ? (sort_direction || 'desc') : undefined);
        setCurrentPage(1);
        setSkeleton(true);
    }, []);

    const handleAgentCreate = useCallback(async (agentData, finish) => {
        try {
            setLoading(true);
            const payload = await createOrUpdateAgent(agentData, agentData?.id);
            const text = resolveBackendMessage(t, payload);
            if (text) message.success(text);
            // Preserve current filters and pagination when refreshing the list
            fetchAgents(currentPage, searchTextRef.current, pagination.pageSize, normalizedFilters);
            setIsModalVisible(false);
            setSelectedAgent(null);
            setModalMode('create');
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
            finish();
        }
    }, [
        currentPage,
        pagination.pageSize,
        normalizedFilters,
        fetchAgents,
        setIsModalVisible,
        setSelectedAgent,
        setModalMode,
        setLoading,
        modalMode,
        handleError,
        t
    ]);

    const handleDelete = useCallback(async (agentId) => {
        try {
            setLoading(true);
            const payload = await deleteAgent(agentId);
            const text = resolveBackendMessage(t, payload);
            if (text) message.success(text);
            // Preserve current filters and pagination when refreshing the list
            fetchAgents(currentPage, searchTextRef.current, pagination.pageSize, normalizedFilters);
        } catch (error) {
            message.error(t('failed_to_delete_agent'));
        } finally {
            setLoading(false);
        }
    }, [
        currentPage,
        pagination.pageSize,
        normalizedFilters,
        fetchAgents,
        setLoading,
        t
    ]);

    const handleStatusChange = useCallback(async (agent, id, onStatusChange) => {
        try {
            onStatusChange(true);
            const payload = await createOrUpdateAgent(agent, id);
            // Preserve current filters and pagination when refreshing the list
            fetchAgents(currentPage, searchTextRef.current, pagination.pageSize, normalizedFilters);
            const text = resolveBackendMessage(t, payload);
            if (text) message.success(text);
        } catch (error) {
            const resolved = resolveBackendMessage(t, error?.response?.data);
            message.error(resolved || t('failed_to_update_agent_status'));
        } finally {
            onStatusChange(false);
        }
    }, [
        fetchAgents,
        currentPage,
        pagination.pageSize,
        normalizedFilters,
        t
    ]);

    const handleSettings = useCallback((agent) => {
        setSelectedAgent(agent);
        setModalMode('edit');
        setIsModalVisible(true);
    }, []);

    const handleClose = () => {
        setIsModalVisible(false);
        setIsAgentGuideOpen(false);
        setSelectedAgent(null);
        setModalMode('create');
    };

    if (!connectionState.status === 'connected') {
        return <div>Socket disconnected. Trying to reconnect...</div>;
    }

    return (
        <Flex
            vertical
            style={{
                flexGrow: 1,
                padding: 0
            }}
        >

            <Card
                style={{
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                }}
                title={t('agents')}
                extra={
                    <Space size={4}>
                        <Button
                            type="link"
                            icon={<PlusOutlined />}
                            size="medium"
                            onClick={() => setIsModalVisible(true)}
                        >
                            {!screens.xs && t('add_agent')}
                        </Button>
                        <Tooltip title={t('guide')}>
                            <Button
                                type="text"
                                icon={<CompassOutlined />}
                                size="medium"
                                onClick={() => {
                                    setIsModalVisible(true);
                                    setIsAgentGuideOpen(true);
                                }}
                            />
                        </Tooltip>
                        <Popover
                            trigger="click"
                            placement="bottomRight"
                            open={isMoreActionsOpen}
                            onOpenChange={setIsMoreActionsOpen}
                            content={
                                <Space direction="vertical" style={{ minWidth: 140 }} onClickCapture={() => setIsMoreActionsOpen(false)}>
                                    <ImportButton resource={Resource.AGENT} type="text" size="small" />
                                    <ExportButton resource={Resource.AGENT} filters={normalizedFilters} type="text" size="small" />
                                </Space>
                            }
                        >
                            <Tooltip title={t('more_actions')}>
                                <Button
                                    type="text"
                                    icon={<EllipsisOutlined />}
                                    size="medium"
                                />
                            </Tooltip>
                        </Popover>
                    </Space>
                }
                styles={{
                    body: {
                        padding: 16
                    }
                }}
            >
                <Flex
                    gap="middle"
                    wrap="wrap"
                    align="center"
                    justify="space-between"
                >
                    <Flex
                        flex={{ xs: '100%', md: 1 }}
                        style={{ flexGrow: 1 }}
                    >
                        <Input
                            prefix={<SearchOutlined />}
                            placeholder={t('search_agents')}
                            size="medium"
                            onChange={handleSearchChange}
                            onFocus={() => setIsInputFocused(true)}
                            onBlur={() => setIsInputFocused(false)}
                            value={searchText}
                            style={{
                                width: isInputFocused ? '100%' : (screens.xs ? '100%' : '400px'),
                                transition: 'width 0.4s ease-in-out'
                            }}
                        />
                    </Flex>

                    <SharedFilterBar
                        fields={agentFilterFields}
                        value={filters}
                        onChange={handleFiltersChange}
                        sortFields={[
                            { value: 'name', labelKey: 'name' },
                            { value: 'is_started', labelKey: 'started' },
                            { value: 'process_audio', labelKey: 'filters_field_process_audio' },
                            { value: 'process_document', labelKey: 'filters_field_process_document' },
                            { value: 'process_image', labelKey: 'filters_field_process_image' },
                            { value: 'process_video', labelKey: 'filters_field_process_video' },
                            { value: 'allow_ticket_creation', labelKey: 'filters_field_allow_ticket_creation' },
                            { value: 'is_consumption_limit_enabled', labelKey: 'filters_field_consumption_limit' },
                            { value: 'max_coins_consumption', labelKey: 'max_coins_consumption' },
                            { value: 'consumption_duration', labelKey: 'consumption_duration' },
                            { value: 'snooze_duration', labelKey: 'snooze_duration' },
                            { value: 'processing_delay', labelKey: 'processing_delay' },
                            { value: 'processing_jitter', labelKey: 'processing_jitter' },
                            { value: 'is_processing_delay_enabled', labelKey: 'filters_field_processing_delay_enabled' },
                            { value: 'created_at', labelKey: 'created_at' },
                            { value: 'updated_at', labelKey: 'updated_at' },
                        ]}
                        sortValue={{ sort_by: sortBy, sort_direction: sortDirection }}
                        onSortChange={handleSortChange}
                    />
                </Flex>
            </Card >

            <Space direction="vertical" style={{ width: '100%', marginTop: 16, flex: 1 }}>
                <AgentList
                    agents={agents}
                    onStatusChange={handleStatusChange}
                    onSettings={handleSettings}
                    onDelete={handleDelete}
                    skeleton={skeleton}
                />
            </Space>

            <Flex justify="center" style={{ marginTop: 16 }}>
                <Pagination
                    current={pagination.current || 1}
                    pageSize={pagination.pageSize || 25}
                    total={pagination.total || 0}
                    onChange={handlePageChange}
                    showSizeChanger
                    pageSizeOptions={[10, 25, 50, 100]}
                    showTotal={(total, range) =>
                        `${range[0]}-${range[1]} of ${total} items`
                    }
                />
            </Flex>
            {
                isModalVisible && <AddAgentModal
                    onClose={handleClose}
                    onCreateAgent={handleAgentCreate}
                    mode={modalMode}
                    initialData={selectedAgent}
                    guideOpen={isAgentGuideOpen}
                    onGuideClose={() => {
                        setIsAgentGuideOpen(false);
                        try { localStorage.setItem('agent_guide_done', '1'); } catch (e) { }
                    }}
                />
            }
        </Flex >
    );
};

export default AgentsPage;
